import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";
import { uniqueness } from "src/utils/array";
import { toUTCDate } from "src/utils/date";
import { stringPresent } from "src/utils/strings";
import { Language } from "src/utils/localization/languages/types";
import { buildDepartureYearsMonths } from "components/PageBuilder/fyc/utils";

export type FYCQuery = string & { _type: "FYCQuery" };

export default class FYCQueryBuilder {
  conditions: string[] = [];

  constructor(initialQuery?: FYCQuery) {
    if (initialQuery) this.appendAndCondition(initialQuery);
  }

  filterByCruiseCodes(cruiseCodes: string[]): FYCQueryBuilder {
    const cruiseCodesQuery = cruiseCodes.map((c) => `objectID:"${c}"`);
    this.appendAndCondition(this.orConditions(cruiseCodesQuery));

    return this;
  }

  filterByDestinationName(
    destinationName: string | undefined,
    language: Language
  ): FYCQueryBuilder {
    if (!destinationName) return this;

    return this.filterByDestinationNames([destinationName], language);
  }

  filterByDestinationNames(
    destinationNames: string[],
    language: Language
  ): FYCQueryBuilder {
    const destinationNamesQuery = destinationNames.map(
      (name) => `destinationNames.${language.sanityCode}:"${name}"`
    );
    this.appendAndCondition(this.orConditions(destinationNamesQuery));

    return this;
  }

  filterByDestinationWebCode(
    destinationWebCode: string | undefined
  ): FYCQueryBuilder {
    if (!destinationWebCode) return this;
    return this.filterByDestinationWebCodes([destinationWebCode]);
  }

  filterByDestinationWebCodes(destinationWebCodes: string[]): FYCQueryBuilder {
    const destinationWebCodesQuery = destinationWebCodes.map(
      (webCode) => `destinationWebCodes:"${webCode}"`
    );
    this.appendAndCondition(this.orConditions(destinationWebCodesQuery));

    return this;
  }

  filterByShipName(shipName: string | undefined): FYCQueryBuilder {
    if (!shipName) return this;

    return this.filterByShipsName([shipName]);
  }

  filterByShipsName(shipNames: string[]): FYCQueryBuilder {
    const shipNamesQuery = shipNames.map(
      (name) => `content.shipName:"${name}"`
    );
    this.appendAndCondition(this.orConditions(shipNamesQuery));

    return this;
  }

  filterByCruiseType(cruiseType: string): FYCQueryBuilder {
    return this.filterByCruiseTypes([cruiseType]);
  }

  filterByCruiseTypes(cruiseTypes: string[]): FYCQueryBuilder {
    const cruiseTypesQuery = cruiseTypes.map((type) => `cruiseType:"${type}"`);
    this.appendAndCondition(this.orConditions(cruiseTypesQuery));

    return this;
  }

  filterByComboType(comboType: string): FYCQueryBuilder {
    return this.filterByComboTypes([comboType]);
  }

  filterByComboTypes(comboTypes: string[]): FYCQueryBuilder {
    const comboTypesQuery = comboTypes.map((type) => `comboType:"${type}"`);
    this.appendAndCondition(this.orConditions(comboTypesQuery));

    return this;
  }

  filterByFareAvailable(fareCode: string): FYCQueryBuilder {
    const fareAvailableQuery = this.andConditions([
      `NOT prices.type:"waitlist"`,
      `prices.webFareCode:"${fareCode}"`
    ]);
    this.appendAndCondition(fareAvailableQuery);
    return this;
  }

  filterByFeatures(features: string[]): FYCQueryBuilder {
    const featuresQuery = features.map((type) => `features:"${type}"`);
    this.appendAndCondition(this.andConditions(featuresQuery));

    return this;
  }

  filterByCruiseTagCategories(
    cruiseTagCategories: string[],
    language: Language
  ): FYCQueryBuilder {
    const cruiseTagCategoriesQuery = cruiseTagCategories.map(
      (category) => `cruiseTagsTree.${language.sanityCode}.level0:"${category}"`
    );
    this.appendAndCondition(this.orConditions(cruiseTagCategoriesQuery));

    return this;
  }

  filterByPorts(portCodes: string[]): FYCQueryBuilder {
    const portsQuery = portCodes.map((portCode) => `portCodes:"${portCode}"`);
    this.appendAndCondition(this.orConditions(portsQuery));

    return this;
  }

  filterByFareCodes(fareCodes: string[]): FYCQueryBuilder {
    const fareCodesQuery = fareCodes
      .filter(uniqueness)
      .map((type) => `fareCodes:"${type}"`);
    this.appendAndCondition(this.orConditions(fareCodesQuery));

    return this;
  }

  filterByFareCode(fareCode: string): FYCQueryBuilder {
    return this.filterByFareCodes([fareCode]);
  }

  filterByCruiseGroups(cruiseGroups: string[]): FYCQueryBuilder {
    const cruiseTypesQuery = cruiseGroups.map(
      (type) => `cruiseGroup:"${type}"`
    );
    this.appendAndCondition(this.orConditions(cruiseTypesQuery));

    return this;
  }

  filterByLandProgrammes(landProgrammesCodes: string[]): FYCQueryBuilder {
    const landProgrammesQuery = landProgrammesCodes.map(
      (landProgramme) => `landProgrammes:"${landProgramme}"`
    );
    this.appendAndCondition(this.orConditions(landProgrammesQuery));

    return this;
  }

  filterByCruiseGroup(cruiseGroup: string): FYCQueryBuilder {
    return this.filterByCruiseGroups([cruiseGroup]);
  }

  filterByPromoCodes(
    promoCodes: number[],
    country: CountryCode | undefined
  ): FYCQueryBuilder {
    if (!country) return this;

    const keyQuery = `countries.${country}.pc`;
    const promos = promoCodes.map((code) => `${keyQuery}:${code}`);
    this.appendAndCondition(this.orConditions(promos));

    return this;
  }

  filterByLoadStress(loadStress: string[]): FYCQueryBuilder {
    const promosTitleQuery = loadStress.map((ls) => `loadStress:"${ls}"`);
    this.appendAndCondition(this.orConditions(promosTitleQuery));
    return this;
  }

  filterByDepartureYears(departureYears: string[]): FYCQueryBuilder {
    const departureYearsQuery = yearsToYearMonths(departureYears);
    this.appendAndCondition(this.orConditions(departureYearsQuery));
    return this;
  }

  filterByDepartureYearsAndMonths(
    items: {
      year?: string | undefined;
      months?: (string | undefined)[] | undefined;
    }[]
  ): FYCQueryBuilder {
    const filterValues = buildDepartureYearsMonths(undefined, items);
    const departureYearsQuery = filterValues.map((yearMonth) => {
      return `departureYearMonth:"${yearMonth}"`;
    });
    this.appendAndCondition(this.orConditions(departureYearsQuery));
    return this;
  }

  onlyVisibleCruises(): FYCQueryBuilder {
    this.appendAndCondition("(visible:true)");
    return this;
  }

  onlyAvailableCruises(): FYCQueryBuilder {
    this.appendAndCondition("(available:true)");
    return this;
  }

  onlyFutureCruises(): FYCQueryBuilder {
    const today = toUTCDate(new Date());
    const todayFilter = Math.floor(today.setHours(23, 59, 59, 0));
    const query = `(departureTimestamp > ${todayFilter})`;
    this.appendAndCondition(query);
    return this;
  }

  private appendAndCondition(condition: string) {
    this.conditions.push(condition);
  }

  private orConditions(conditions: string[]) {
    return `(${conditions.join(" OR ")})`;
  }

  private andConditions(conditions: string[]) {
    return `(${conditions.join(" AND ")})`;
  }

  generate(): FYCQuery {
    return this.conditions
      .filter(this.filterEmptyCondition)
      .join(" AND ") as FYCQuery;
  }

  private filterEmptyCondition(condition: string): boolean {
    return stringPresent(condition) && condition != "()";
  }
}

const yearsToYearMonths = (years: string[]): string[] => {
  const monts = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );
  return years.flatMap((year) =>
    monts.map((m) => `departureYearMonth:"${year}-${m}"`)
  );
};
